import { Component, OnInit, OnDestroy, ChangeDetectorRef, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription, firstValueFrom } from 'rxjs';
import { NgbPaginationModule, NgbTooltipModule, NgbAlertModule, NgbDropdownModule, NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { AuthService } from '../../services/auth.service';
import { AuthUser } from '../../interfaces/auth.interface';
import { StaffHeaderComponent } from '../staff-header/staff-header.component';
import { StaffNavComponent } from '../staff-nav/staff-nav.component';
import { environment } from '../../../environments/environment';
import { UserInformationSummary, getPhoneNumberFrom } from '../../interfaces/user-information-summary.interface';
import { documentStatus } from '../../services/statusList';
import { isLifetimeMemberType, LIFETIME_MEMBER_LABEL } from '../../utils/membership.util';
import { csvFileDateSuffix, downloadCsv } from '../../utils/csv.util';

// Request Registration Interface (API Response)
export interface RequestRegistration {
  id: string;                           // เลขที่คำขอ
  user: {
    cid: string;                        // ID Card No.
    email?: string;                     // อีเมลผู้ยื่นคำขอ
    userInformation: UserInformationSummary;
  };
  paymentSlip?: string | null;          // หลักฐานการชำระเงิน
  codeReceipt?: string | null;          // รหัสใบเสร็จ (ว่าง = ยังไม่ออกใบเสร็จ)
  membershipType: string;               // ประเภทสมาชิก
  workExperienceYears: number;          // จำนวนปีที่สมัคร
  feeAmount: number;                    // จำนวนเงินที่ต้องชำระ
  paymentStatus?: string;              // สถานะการชำระเงิน
  status: string;                       // สถานะการดำเนินการ
  createdAt?: string;
  updatedAt?: string;
}

// API Response Interface
export interface ApiResponse {
  data: RequestRegistration[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// Filters Interface
export interface RequestFilters {
  searchTerm: string;
  requestNo: string;
  idCardNo: string;
  organization: string;
  vatMemberType: string;
  requestStatus: string;
  dateFrom: string;
  dateTo: string;
}

/** แท็บกรองตามสถานะการดำเนินการ พร้อมจำนวนคำขอในแต่ละสถานะ */
interface RequestStatusTab {
  /** '' = แท็บรวมทุกสถานะ ค่าอื่นคือค่า status ที่ส่งให้ API ตรง ๆ */
  value: string;
  name: string;
  /** ชื่อสีของ bootstrap ที่ใช้ทั้งกับแท็บและ badge ในตาราง */
  color: string;
  count: number;
}

@Component({
  selector: 'app-request-regis-new-user-management',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgbPaginationModule,
    NgbTooltipModule,
    NgbAlertModule,
    NgbDropdownModule,
    StaffHeaderComponent,
    StaffNavComponent
  ],
  providers: [],
  templateUrl: './request-regis-new-user-management.component.html',
  styleUrl: './request-regis-new-user-management.component.scss'
})
export class RequestRegisNewUserManagementComponent implements OnInit, OnDestroy {
  currentUser: AuthUser | null = null;
  isMobileMenuOpen = false;
  private userSubscription: Subscription | undefined;

  requests: RequestRegistration[] = [];
  filteredRequests: RequestRegistration[] = [];
  isLoading = false;  // Start with false to avoid initial loading spinner
  isExporting = false;
  error: string | null = null;
  showMockDataWarning = false;

  // Filter properties
  filters: RequestFilters = {
    searchTerm: '',
    requestNo: '',
    idCardNo: '',
    organization: '',
    vatMemberType: '',
    requestStatus: 'all',
    dateFrom: '',
    dateTo: ''
  };

  /**
   * แท็บแยกตามสถานะการดำเนินการ สร้างจาก documentStatus ที่ใช้ร่วมกับหน้าฟอร์ม
   * รายละเอียดคำขอ จึงไม่มีการเพิ่มสถานะใหม่เองในหน้านี้ แท็บแรกคือ "ทั้งหมด" เสมอ
   */
  statusTabs: RequestStatusTab[] = [];

  /** จำนวนรายการต่อหนึ่งครั้งที่ขอจาก API (หน้านี้กรอง/แบ่งหน้าฝั่ง client) */
  private readonly REQUEST_PAGE_SIZE = 500;

  /** เพดานจำนวนหน้าที่ยอมวนขอ กันลูปไม่รู้จบเมื่อ API ตอบผิดรูปแบบ */
  private readonly REQUEST_MAX_PAGES = 100;

  /** สีของแต่ละสถานะ ใช้ร่วมกันระหว่างแท็บกับ badge ในตาราง */
  private readonly statusColors: Record<string, string> = {
    'รอตรวจเอกสาร': 'secondary',
    'ผ่านการตรวจสอบ': 'info',
    'ดำเนินการออกบัตร': 'warning',
    'ดำเนินการเสร็จสิ้น': 'success',
    'ยกเลิก': 'danger'
  };

  // Pagination
  page = 1;
  pageSize = 10;
  readonly pageSizeOptions = [10, 20, 50, 100];
  collectionSize = 0;

  // Sorting
  sortField = '';
  sortDirection: 'asc' | 'desc' = 'asc';

  private subscription = new Subscription();

  constructor(
    private authService: AuthService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private modalService: NgbModal,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    console.log('🔄 RequestRegisNewUserManagementComponent ngOnInit');

    // Subscribe to auth user
    this.userSubscription = this.authService.currentUser$.subscribe(user => {
      console.log('👤 Current user updated:', user);
      this.currentUser = user;
      this.cdr.detectChanges();
    });

    // Load data immediately without loading spinner on initial load
    console.log('📥 Loading data immediately on init');
    this.rebuildStatusTabs(); // ขึ้นแท็บทันที แล้วค่อยเติมจำนวนเมื่อโหลดข้อมูลเสร็จ
    this.loadRequests();
  }

  ngOnDestroy(): void {
    console.log('🗑️ RequestRegisNewUserManagementComponent ngOnDestroy');
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
    this.subscription.unsubscribe();
  }

  onLogout(): void {
    this.authService.logout();
  }

  closeMobileMenu(): void {
    this.isMobileMenuOpen = false;
  }

  onNavigate(route: string): void {
    this.closeMobileMenu();

    switch (route) {
      case 'users':
        this.router.navigate(['/staff/user-management']);
        break;
      case 'courses':
        this.router.navigate(['/staff/dashboard']);
        break;
      case 'reports':
        this.router.navigate(['/staff/dashboard']);
        break;
      case 'settings':
        this.router.navigate(['/staff/dashboard']);
        break;
      default:
        this.router.navigate(['/staff/dashboard']);
        break;
    }
  }

  /**
   * Load all registration requests from API
   */
  loadRequests(): void {
    this.isLoading = true;
    this.error = null;
    this.showMockDataWarning = false;
    this.cdr.detectChanges();

    this.fetchAllRequests()
      .then(requests => {
        this.requests = requests;
        this.isLoading = false;
        // กรองและนับจำนวนในแท็บจากรายการทั้งหมด ไม่ใช่เฉพาะหน้าที่แสดงอยู่
        this.applyFilters();
      })
      .catch((error: HttpErrorResponse) => {
        console.error('❌ API Error:', error);
        this.error = `เกิดข้อผิดพลาดในการโหลดข้อมูล: ${error.message}`;
        this.requests = [];
        this.isLoading = false;
        this.applyFilters();
      });
  }

  /**
   * ดึงคำขอมาให้ครบทุกรายการ
   *
   * เดิมหน้านี้ขอมาทีละหน้าแล้วกรองฝั่ง client ตัวกรองจึงทำงานแค่กับรายการใน
   * หน้าที่เปิดอยู่ ผลลัพธ์บนตารางกับในไฟล์ CSV จึงไม่ตรงกัน
   * ตอนนี้ดึงมาทั้งหมดครั้งเดียว แล้วกรอง/เรียง/แบ่งหน้าฝั่ง client ทั้งหมด
   */
  private async fetchAllRequests(): Promise<RequestRegistration[]> {
    const collected: RequestRegistration[] = [];
    let page = 1;
    let total = 0;

    do {
      const apiUrl = `${environment.apiUrl}/form-request-new-member?page=${page}` +
        `&limit=${this.REQUEST_PAGE_SIZE}${this.getSortQuery()}`;
      const response = await firstValueFrom(this.http.get<ApiResponse>(apiUrl));

      const rows = response?.data || [];
      collected.push(...rows);
      total = typeof response?.total === 'number' ? response.total : collected.length;

      // API ไม่ส่งข้อมูลเพิ่มแล้ว หยุดทันทีกันลูปไม่รู้จบ
      if (rows.length === 0) break;
      page += 1;
    } while (collected.length < total && page <= this.REQUEST_MAX_PAGES);

    if (collected.length < total) {
      console.warn(`⚠️ โหลดคำขอได้ ${collected.length} จาก ${total} รายการ (ชนเพดานจำนวนหน้า)`);
    }

    return collected;
  }

  /**
   * Handle page change - reload data from API
   */
  onPageChange(newPage: number): void {
    this.page = newPage;
    this.cdr.detectChanges();
  }

  /**
   * Apply filters to request list (client-side filtering)
   */
  applyFilters(): void {
    try {
      this.filteredRequests = (this.requests || []).filter(
        request => this.matchesFilters(request) && this.matchesStatusFilter(request)
      );
    } catch (error) {
      console.error('❌ Error in applyFilters:', error);
      this.filteredRequests = [...(this.requests || [])];
    }

    this.collectionSize = this.filteredRequests.length;
    this.page = 1;
    this.rebuildStatusTabs();
    this.cdr.detectChanges();
  }

  /** กรองตามแท็บสถานะที่เลือกอยู่ ('all' = ทุกสถานะ) */
  private matchesStatusFilter(request: RequestRegistration): boolean {
    const status = this.filters.requestStatus;
    if (!status || status === 'all') return true;
    return request.status === status;
  }

  /**
   * เงื่อนไขตัวกรอง (ใช้ร่วมกันระหว่างตารางและการ Export CSV)
   */
  private matchesFilters(request: RequestRegistration): boolean {
    // Search term
    if (this.filters.searchTerm) {
      const searchLower = this.filters.searchTerm.toLowerCase();
      const matchesSearch =
        (request.user.userInformation.firstName || '').toLowerCase().includes(searchLower) ||
        (request.user.userInformation.lastName || '').toLowerCase().includes(searchLower) ||
        request.id.toLowerCase().includes(searchLower) ||
        request.user.cid.includes(searchLower) ||
        this.getApplicantName(request).toLowerCase().includes(searchLower) ||
        this.getApplicantOrganization(request).toLowerCase().includes(searchLower);

      if (!matchesSearch) return false;
    }

    // Request No
    if (this.filters.requestNo && !request.id.toLowerCase().includes(this.filters.requestNo.toLowerCase())) {
      return false;
    }

    // ID Card No
    if (this.filters.idCardNo && !request.user.cid.includes(this.filters.idCardNo)) {
      return false;
    }

    // VAT Member Type
    if (this.filters.vatMemberType && request.membershipType !== this.filters.vatMemberType) {
      return false;
    }

    return true;
  }

  /**
   * Get current page requests
   * Since we're using server-side pagination, the API already returns only the current page's data
   */
  getCurrentPageRequests(): RequestRegistration[] {
    const startIndex = (this.page - 1) * this.pageSize;
    return (this.filteredRequests || []).slice(startIndex, startIndex + this.pageSize);
  }

  /**
   * ตรวจสอบว่าเป็นคำขอประเภทสมาชิกตลอดชีพหรือไม่
   */
  isLifetimeMembership(membershipType: string): boolean {
    return isLifetimeMemberType(membershipType);
  }

  /**
   * ข้อความจำนวนปีที่สมัคร (สมาชิกตลอดชีพไม่มีจำนวนปี)
   */
  getMembershipYearsText(request: RequestRegistration): string {
    if (this.isLifetimeMembership(request.membershipType)) {
      return LIFETIME_MEMBER_LABEL;
    }

    return `${request.workExperienceYears || 0} ปี`;
  }

  /**
   * ชื่อ-นามสกุลผู้ยื่นคำขอ
   */
  getApplicantName(request: RequestRegistration): string {
    const info = request?.user?.userInformation;
    const first = info?.firstName || info?.firstNameThai || '';
    const last = info?.lastName || info?.lastNameThai || '';
    return `${first} ${last}`.trim() || '-';
  }

  /**
   * บริษัท/หน่วยงานของผู้ยื่นคำขอ
   */
  getApplicantOrganization(request: RequestRegistration): string {
    const info = request?.user?.userInformation;
    return info?.company?.organizationName || info?.workplaceName || info?.organization || '-';
  }

  /**
   * วันและเวลาที่ผู้ใช้ยื่นคำขอเข้ามาในระบบ
   */
  formatDateTime(dateString?: string): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';

    return `${date.toLocaleDateString('th-TH')} ${date.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })} น.`;
  }

  /**
   * มีหลักฐานการชำระเงินที่ผู้สมัครอัปโหลดไว้หรือไม่
   */
  hasPaymentSlip(request: RequestRegistration): boolean {
    return !!request?.paymentSlip && String(request.paymentSlip).trim() !== '';
  }

  /**
   * เปิดดูหลักฐานการชำระเงิน
   */
  openPaymentSlip(request: RequestRegistration, event: Event): void {
    event.stopPropagation();
    if (this.hasPaymentSlip(request)) {
      window.open(request.paymentSlip as string, '_blank');
    }
  }

  /**
   * สถานะการออกใบเสร็จ (รอดำเนินการ / ออกใบเสร็จแล้ว)
   */
  getReceiptStatusText(request: RequestRegistration): string {
    return request?.codeReceipt ? 'ออกใบเสร็จแล้ว' : 'รอดำเนินการ';
  }

  getReceiptStatusClass(request: RequestRegistration): string {
    return request?.codeReceipt ? 'bg-success' : 'bg-secondary';
  }

  /**
   * Get member status based on vatId and vatExpired
   */
  getMemberStatus(request: RequestRegistration): string {
    const vatId = request.user.userInformation.vatId;
    const vatExpired = request.user.userInformation.vatExpired;

    // Check if no vatId
    if (!vatId || vatId.trim() === '') {
      return 'ยังไม่มีบัตรสมาชิก';
    }

    // สมาชิกตลอดชีพไม่มีวันหมดอายุ
    if (this.isLifetimeMembership(request.membershipType)) {
      return 'Active';
    }

    // Check if expired
    if (vatExpired) {
      const expiredDate = new Date(vatExpired);
      const now = new Date();
      
      if (now > expiredDate) {
        return 'หมดอายุ';
      }
    }

    return 'Active';
  }

  /**
   * Clear all filters
   */
  clearFilters(): void {
    this.filters = {
      searchTerm: '',
      requestNo: '',
      idCardNo: '',
      organization: '',
      vatMemberType: '',
      requestStatus: 'all',
      dateFrom: '',
      dateTo: ''
    };
    this.page = 1;
    this.applyFilters();
  }

  /**
   * Sort requests by field
   */
  /**
   * คอลัมน์ที่เรียงลำดับได้ (ต้องตรงกับฝั่ง backend)
   */
  private readonly sortableFields = [
    'createdAt', 'idCardNo', 'applicantName', 'organization', 'feeAmount',
    'paymentStatus', 'paymentSlip', 'receiptStatus', 'processingStatus',
    'memberStatus', 'vatId'
  ];

  /**
   * กดหัวคอลัมน์เพื่อเรียงลำดับ
   * กดครั้งแรก = น้อยไปมาก (asc), กดซ้ำ = มากไปน้อย (desc), กดครั้งที่สาม = ยกเลิกการเรียง
   */
  sortBy(field: string): void {
    if (!this.sortableFields.includes(field)) {
      return;
    }

    if (this.sortField !== field) {
      this.sortField = field;
      this.sortDirection = 'asc';
    } else if (this.sortDirection === 'asc') {
      this.sortDirection = 'desc';
    } else {
      // กดครบรอบ → กลับไปใช้ลำดับเริ่มต้น (ล่าสุดอยู่บนสุด)
      this.sortField = '';
      this.sortDirection = 'asc';
    }

    // เรียงจากฝั่ง server เพื่อให้ครอบคลุมข้อมูลทุกหน้า ไม่ใช่เฉพาะหน้าที่เปิดอยู่
    this.page = 1;
    this.loadRequests();
  }

  /**
   * query string สำหรับการเรียงลำดับ
   */
  private getSortQuery(): string {
    if (!this.sortField) return '';
    return `&sortBy=${this.sortField}&sortOrder=${this.sortDirection.toUpperCase()}`;
  }

  /**
   * เรียงลำดับอยู่ที่คอลัมน์นี้หรือไม่
   */
  isSortedBy(field: string): boolean {
    return this.sortField === field;
  }

  getSortIcon(field: string): string {
    if (this.sortField !== field) return '⇅';
    return this.sortDirection === 'asc' ? '▲' : '▼';
  }

  /**
   * Change page size and reload data
   */
  changePageSize(size: number): void {
    this.pageSize = size;
    this.page = 1;
    this.cdr.detectChanges();
  }

  /**
   * Refresh data
   */
  refreshData(): void {
    console.log('🔄 Refreshing data...');
    this.loadRequests();
  }

  /**
   * Export to CSV
   */
  exportToCSV(): void {
    if (this.isExporting) {
      return;
    }

    // หน้านี้โหลดข้อมูลมาทั้งหมดแล้ว ไฟล์ CSV จึงตรงกับที่เห็นบนตารางเสมอ
    const rows = this.filteredRequests || [];
    if (rows.length === 0) {
      alert('ไม่พบข้อมูลที่ตรงกับเงื่อนไขการกรอง');
      return;
    }

    this.isExporting = true;
    this.cdr.detectChanges();

    this.downloadRequestsCsv(rows);

    this.isExporting = false;
    this.cdr.detectChanges();
  }

  /**
   * สร้างไฟล์ CSV จากรายการคำขอ (คอลัมน์ตรงกับตารางบนหน้าจอ)
   */
  private downloadRequestsCsv(requests: RequestRegistration[]): void {
    const headers = [
      'วันและเวลาที่ยื่นคำขอ',
      'ID Card No.',
      'ชื่อ-นามสกุล',
      'บริษัท/หน่วยงาน',
      'จำนวนชำระ (บาท)',
      'สถานะการชำระเงิน',
      'หลักฐานการชำระเงิน',
      'สถานะการออกใบเสร็จ',
      'สถานะการดำเนินการ',
      'สถานะสมาชิก',
      'เลขที่สมาชิก',
      'เลขที่คำขอ',
      // ข้อมูลติดต่อเพิ่มเติม (ไม่ได้แสดงบนตาราง)
      'อีเมล',
      'เบอร์โทร'
    ];

    const rows = requests.map(request => [
      this.formatDateTime(request.createdAt),
      request.user.cid,
      this.getApplicantName(request),
      this.getApplicantOrganization(request),
      Number(request.feeAmount || 0).toFixed(2),
      request.paymentStatus || '-',
      this.hasPaymentSlip(request) ? 'มีหลักฐาน' : 'ไม่มีหลักฐาน',
      this.getReceiptStatusText(request),
      this.getStatusText(request.status),
      this.getMemberStatus(request),
      request.user.userInformation.vatId || '-',
      request.id,
      request.user.email || '-',
      getPhoneNumberFrom(request.user.userInformation)
    ]);

    downloadCsv(`request-regis-new-member_${csvFileDateSuffix()}`, headers, rows);
    console.log(`✅ Exported ${rows.length} rows to CSV`);
  }

  /**
   * Format date for display
   */
  formatDate(dateString: string): string {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('th-TH');
  }


  // ── แท็บแยกสถานะการดำเนินการ ──

  /**
   * สร้างแท็บสถานะใหม่จากจำนวนที่นับได้ล่าสุด
   */
  private rebuildStatusTabs(): void {
    // นับจากรายการที่ผ่านตัวกรองอื่นแล้ว ผู้ใช้จึงเห็นล่วงหน้าว่ากดแท็บไหนจะได้กี่รายการ
    const matches = (this.requests || []).filter(request => this.matchesFilters(request));

    this.statusTabs = [
      { value: '', name: 'ทั้งหมด', color: 'dark', count: matches.length },
      ...documentStatus.map(name => ({
        value: name,
        name,
        color: this.getStatusColor(name),
        count: matches.filter(request => request.status === name).length
      }))
    ];
  }

  /** เลือกแท็บสถานะ */
  selectStatusTab(tab: RequestStatusTab): void {
    if (this.isActiveStatusTab(tab)) return;

    this.filters.requestStatus = tab.value || 'all';
    this.applyFilters();
  }

  isActiveStatusTab(tab: RequestStatusTab): boolean {
    const current = this.filters.requestStatus === 'all' ? '' : this.filters.requestStatus;
    return current === tab.value;
  }


  private getStatusColor(statusName?: string): string {
    return this.statusColors[statusName || ''] || 'secondary';
  }

  /**
   * Get status badge class
   */
  getStatusBadgeClass(status: string): string {
    return `bg-${this.getStatusColor(status)}`;
  }

  /**
   * Get status text
   */
  getStatusText(status: string): string {
    return status || '-';
  }

  /**
   * View request details
   */
  viewRequest(request: RequestRegistration): void {
    console.log('👁️ View request:', request);
    console.log('📍 Navigating to form with request ID:', request.id);
    // Navigate to request detail form with requestId
    this.router.navigate(['/staff/request-regis-new-member-form', request.id]);
  }

  /**
   * Approve request
   */
  approveRequest(request: RequestRegistration): void {
    console.log('✅ Approve request:', request);
    const confirmation = confirm(`คุณต้องการอนุมัติคำขอ ${request.id} หรือไม่?`);
    
    if (confirmation) {
      request.status = 'approved';
      this.cdr.detectChanges();
      alert(`✅ อนุมัติคำขอ ${request.id} เรียบร้อยแล้ว`);
      // TODO: Send update to API
    }
  }

  /**
   * Reject request
   */
  rejectRequest(request: RequestRegistration): void {
    console.log('❌ Reject request:', request);
    const reason = prompt(`กรุณาระบุเหตุผลในการไม่อนุมัติคำขอ ${request.id}:`);
    
    if (reason) {
      request.status = 'rejected';
      this.cdr.detectChanges();
      alert(`❌ ไม่อนุมัติคำขอ ${request.id} เรียบร้อยแล้ว`);
      // TODO: Send update to API
    }
  }

  /**
   * Edit request
   */
  editRequest(request: RequestRegistration): void {
    console.log('🔧 Edit request:', request);
    // TODO: Implement edit request modal
    alert(`แก้ไขคำขอ: ${request.id}`);
  }

  /**
   * Delete request
   */
  deleteRequest(request: RequestRegistration): void {
    console.log('🗑️ Delete request:', request);
    const confirmation = confirm(`คุณต้องการลบคำขอ ${request.id} หรือไม่?\n\nการดำเนินการนี้ไม่สามารถยกเลิกได้!`);
    
    if (confirmation) {
      const index = this.requests.findIndex(r => r.id === request.id);
      if (index > -1) {
        this.requests.splice(index, 1);
        this.applyFilters();
        alert(`✅ ลบคำขอ ${request.id} เรียบร้อยแล้ว`);
      }
    }
  }

  /**
   * Track by function for ngFor optimization
   */
  trackByRequestId(index: number, request: RequestRegistration): string {
    return request.id;
  }

  /**
   * Math helper for template
   */
  min(a: number, b: number): number {
    return Math.min(a, b);
  }

  /**
   * Handle dropdown open/close state
   */
  onDropdownOpenChange(isOpen: boolean, requestId: string): void {
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        const dropdownButton = document.getElementById(`dropdownMenuButton-${requestId}`);
        const tableRow = dropdownButton?.closest('tr');
        const tableContainer = dropdownButton?.closest('.table-responsive');

        if (tableRow) {
          if (isOpen) {
            tableRow.classList.add('dropdown-active');
            tableRow.style.position = 'relative';
            tableRow.style.zIndex = '1050';

            if (tableContainer) {
              (tableContainer as HTMLElement).style.overflow = 'visible';
            }

            const rowRect = tableRow.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const dropdownMenu = tableRow.querySelector('.dropdown-menu') as HTMLElement;

            if (dropdownMenu && rowRect.bottom > viewportHeight - 200) {
              dropdownMenu.style.top = 'auto';
              dropdownMenu.style.bottom = '100%';
              dropdownMenu.style.marginBottom = '2px';
              dropdownMenu.style.marginTop = '0';
            }
          } else {
            tableRow.classList.remove('dropdown-active');
            tableRow.style.position = '';
            tableRow.style.zIndex = '';

            if (tableContainer && window.innerWidth >= 992) {
              (tableContainer as HTMLElement).style.overflow = 'visible';
            } else if (tableContainer) {
              (tableContainer as HTMLElement).style.overflow = '';
            }

            const dropdownMenu = tableRow.querySelector('.dropdown-menu') as HTMLElement;
            if (dropdownMenu) {
              dropdownMenu.style.top = '';
              dropdownMenu.style.bottom = '';
              dropdownMenu.style.marginBottom = '';
              dropdownMenu.style.marginTop = '';
            }
          }
        }
      }, 0);
    });
  }
}
